import React, { useState } from 'react';

import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { IconButton, Theme, Typography, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';

import IntelGridTabs from './components/IntelGridTabs';
import Legend from './components/Legend';
import ArmyGrid from './gridViews/ArmyGrid';
import EnemyOpsGrid from './gridViews/EnemyOpsGrid';
import KingdomOpsGrid from './gridViews/KingdomOpsGrid';
import MainGrid from './gridViews/MainGrid';
import WarGrid from './gridViews/WarGrid';
import { Tab } from '../../@types/State';
import { useAppState } from '../../context/AppState';
import { useIntelState } from '../../context/IntelState';
import useBuildingBookNames from '../../hooks/useBuildingBookNames';
import EnemySwitcher from '../Common/EnemySwitcher';

const Intel = () => {
  const location = useLocation();
  const { intelState } = useIntelState();
  const { appDispatch } = useAppState();
  const matchesSmallWidth = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );
  const [currentTab, setCurrentTab] = useState<Tab | 'gains'>('overview');
  const [zoom, setZoom] = useState(isMobile ? 75 : 100);
  const isViewingEnemy: boolean =
    location.pathname === '/enemy' || location.pathname === '/enemyops';
  const [bookNames, buildNames] = useBuildingBookNames();
  const dataSet = isViewingEnemy ? intelState.enemyData : intelState.ownData;

  const getView = () => {
    switch (currentTab) {
      case 'ops':
        return isViewingEnemy ? <EnemyOpsGrid /> : <KingdomOpsGrid />;
      case 'gains':
        return <WarGrid />;
      case 'armies':
        return <ArmyGrid dataSet={dataSet?.provinces} />;
      default:
        return (
          <MainGrid
            dataSet={dataSet?.provinces}
            plan={isViewingEnemy ? intelState.enemyPlan : intelState.ownPlan}
            currentTab={currentTab}
            isViewingEnemy={isViewingEnemy}
            zoom={zoom}
          />
        );
    }
  };

  const ownKd = intelState.ownData ?? undefined;
  const enemyKd = intelState.enemyData ?? undefined;

  return (
    <Box mt={isMobile ? 0 : 2} className='Home'>
      <Box
        sx={{
          height: '50px',
          display: { xs: 'flex' },
          alignItems: 'center',
          justifyContent: 'space-between',
          marginX: 1,
        }}
      >
        {isViewingEnemy && <EnemySwitcher />}
        <Typography
          variant='h3'
          component='span'
          sx={{
            maxWidth: isMobile ? (isViewingEnemy ? 190 : 250) : '100%',
          }}
        >
          {isViewingEnemy ? enemyKd?.kingdomName : ownKd?.kingdomName}
        </Typography>
        {isMobile && (
          <Box>
            <IconButton
              aria-label='zoom in'
              onClick={() => {
                const newZoom = Math.min(zoom + 25, 100);
                setZoom(newZoom);
                appDispatch({
                  type: 'SET_ZOOM',
                  payload: newZoom === 75 ? 'default' : 'in',
                });
              }}
              disabled={zoom === 100}
              sx={{ padding: 0, mr: 1 }}
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              aria-label='zoom out'
              onClick={() => {
                const newZoom = Math.max(zoom - 25, 50);
                setZoom(newZoom);
                appDispatch({
                  type: 'SET_ZOOM',
                  payload: newZoom === 75 ? 'default' : 'out',
                });
              }}
              disabled={zoom === 50}
              sx={{ padding: 0 }}
            >
              <ZoomOutIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      <div
        style={{
          zoom: `${zoom || 100}%`,
        }}
      >
        <IntelGridTabs
          currentTab={currentTab}
          handleChangeTab={(e: any, newValue: any) => setCurrentTab(newValue)}
          hasBookNames={!!bookNames}
          hasBuildingNames={!!buildNames}
        />

        <Box mx={1} mb={2} style={{ minHeight: 200 }}>
          <div style={{ width: '100%' }}>{getView()}</div>
        </Box>
      </div>
      <Legend useBlocks={matchesSmallWidth} />
    </Box>
  );
};

export default Intel;
